import {
  activateCompanyApi,
  addCompanyMemberApi,
  deactivateCompanyApi,
  getCompanyGeneralSettingsApi,
  getCompanyMembersApi,
  removeCompanyMemberApi,
  updateCompanyGeneralSettingsApi,
  updateCompanyPackagesApi,
  updateCompanyPlatformFeaturesApi,
  updateCompanyPlatformFeaturesMetadataApi,
} from '@api/companyApi';
import { client } from '@api/rest/client';
import { Actions } from '@constants/actionTypes';
import { SELECT_CUSTOMER_ROUTE } from '@constants/routes';
import type {
  BulkInviteUsersDTO,
  CreateCompanyDTO,
  ProvisionStatusResponseDTO,
} from '@conversed/rest/src/contracts/company/company.contract';
import type {
  CompanyGeneralSettingsDTO,
  CompanyMemberDTO,
  CompanyPackagesDTO,
  CreateMemberDTO,
} from '@conversed/shared/src/central/dto/company';
import type {
  PlatformFeaturesMetadata,
  PlatformFeatureType,
} from '@conversed/shared/src/central/types/platform-features.type';
import {
  toastErrorTranslated,
  toastSuccessTranslated,
  toastWarningTranslated,
} from '@helpers/toast';
import { useFileMedia } from '@hooks/useFileMedia';
import { EApiStatus } from '@interfaces/EApiStatus';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useCurrentUserActions } from '../../auth/useCurrentUserActions';
import { useMainDispatch } from '../../useMainDispatch';

export type TCompanyMembersLoad =
  | { status: EApiStatus.SUCCESS; members: CompanyMemberDTO[] }
  | { status: EApiStatus.ERROR };

type TTeardownOutcome = 'success' | 'deprovision-failed' | 'unexpected-status';

// * A partial rollback failure still answers 200 with the company left in
// * deprovision_failed, so the returned status is the only signal that says
// * whether every resource actually went away - a 200 alone does not.
const resolveTeardownOutcome = (
  data: ProvisionStatusResponseDTO,
): TTeardownOutcome => {
  if (data?.status === 'deprovisioned') {
    return 'success';
  }

  if (data?.status === 'deprovision_failed') {
    return 'deprovision-failed';
  }

  return 'unexpected-status';
};

export const useCompanySettingsActions = () => {
  const dispatch = useMainDispatch();
  const newDispatch = useDispatch();
  const navigate = useNavigate();
  const { fetchCurrentUser } = useCurrentUserActions();
  const { getImageByURL } = useFileMedia();

  // * Does not write the store: the caller drops a response that arrives after
  // * a company switch, so another company's members never reach the store.
  const fetchCompanyMembers =
    useCallback(async (): Promise<TCompanyMembersLoad> => {
      try {
        const { data: members } = await getCompanyMembersApi();

        return { status: EApiStatus.SUCCESS, members };
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);

        return { status: EApiStatus.ERROR };
      }
    }, []);

  const setCompanyMembers = useCallback(
    (members: CompanyMemberDTO[]) => {
      dispatch({ type: Actions.SET_COMPANY_SETTINGS, payload: { members } });
    },
    [dispatch],
  );

  const fetchGeneralSettings = useCallback(async () => {
    try {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
      const { data: generalSettings } = await getCompanyGeneralSettingsApi();
      dispatch({
        type: Actions.SET_COMPANY_SETTINGS,
        payload: {
          generalSettings: {
            ...generalSettings,
            icon: getImageByURL(generalSettings?.icon as string),
          },
        },
      });
    } catch (error) {
      console.error(error);
      toastErrorTranslated(error);
    } finally {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
    }
  }, [dispatch, getImageByURL]);

  const updateGeneralSettings = useCallback(
    async (generalSettingsData: CompanyGeneralSettingsDTO) => {
      try {
        const { data: generalSettings } =
          await updateCompanyGeneralSettingsApi(generalSettingsData);
        dispatch({
          type: Actions.SET_COMPANY_SETTINGS,
          payload: { generalSettings },
        });
        fetchCurrentUser();
        toastSuccessTranslated('Success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [dispatch, fetchCurrentUser],
  );

  // * Returns the new company's id so the caller can route to the provisioning
  // * page (provision:true) or the selector (manual create); navigation lives at
  // * the call site because it depends on that provision choice.
  const createCompany = useCallback(
    async (companyData: CreateCompanyDTO): Promise<string | undefined> => {
      try {
        const { data } = await client.company.createCompany({
          body: companyData,
        });
        fetchCurrentUser();
        toastSuccessTranslated('Success');
        return data.companyId;
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
        return undefined;
      }
    },
    [fetchCurrentUser],
  );

  const addMember = useCallback(
    async (memberData: CreateMemberDTO) => {
      try {
        const { data: member } = await addCompanyMemberApi(memberData);
        dispatch({ type: Actions.ADD_COMPANY_MEMBER, payload: member });
        toastSuccessTranslated('Success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [dispatch],
  );

  const activateCompany = useCallback(async () => {
    try {
      await activateCompanyApi();
      fetchCurrentUser();
      navigate(SELECT_CUSTOMER_ROUTE);
      toastSuccessTranslated('Success');
    } catch (error) {
      console.error(error);
      toastErrorTranslated(error);
    }
  }, [fetchCurrentUser, navigate]);

  const deactivateCompany = useCallback(async () => {
    try {
      await deactivateCompanyApi();
      fetchCurrentUser();
      navigate(SELECT_CUSTOMER_ROUTE);
      toastSuccessTranslated('Success');
    } catch (error) {
      console.error(error);
      toastErrorTranslated(error);
    }
  }, [fetchCurrentUser, navigate]);

  // * SuperAdmin-only teardown - tears down the company's provisioned
  // * infrastructure. Unlike deactivateCompany this hits the provisioning
  // * deprovision endpoint directly, since companyId isn't implicit there.
  // * Navigation is the caller's business: the provisioning curtain issues this
  // * and keeps the user on the status page for the whole unwind.
  const teardownCompany = useCallback(
    async (companyId: string) => {
      try {
        const { data } = await client.company.deprovision({
          params: { companyId },
          body: undefined,
        });
        fetchCurrentUser();

        const outcome = resolveTeardownOutcome(data);

        if (outcome === 'success') {
          toastSuccessTranslated(
            'general-settings.lifecycle-modal.teardown-toast.success',
          );

          return;
        }

        if (outcome === 'deprovision-failed') {
          toastErrorTranslated(
            'general-settings.lifecycle-modal.teardown-toast.deprovision-failed',
          );

          return;
        }

        toastWarningTranslated(
          'general-settings.lifecycle-modal.teardown-toast.unexpected-status',
        );
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [fetchCurrentUser],
  );

  const removeMember = useCallback(
    async (memberId: string) => {
      try {
        await removeCompanyMemberApi(memberId);
        newDispatch(mainActions.removeCompanyMember(memberId));
        toastSuccessTranslated('Success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [newDispatch],
  );

  const updateCompanyPackages = useCallback(
    async (packages: CompanyPackagesDTO) => {
      try {
        const { data } = await updateCompanyPackagesApi(packages);

        dispatch({
          type: Actions.UPDATE_COMPANY_PACKAGES_SUCCESS,
          payload: data,
        });

        toastSuccessTranslated('Success');
        return data;
      } catch (error: any) {
        toastErrorTranslated(error);
        return null;
      }
    },
    [dispatch],
  );

  const updateCompanyPlatformFeaturesMetadata = useCallback(
    async (metadata: PlatformFeaturesMetadata) => {
      try {
        const { data } =
          await updateCompanyPlatformFeaturesMetadataApi(metadata);

        dispatch({
          type: Actions.UPDATE_COMPANY_PLATFORM_FEATURES_METADATA_SUCCESS,
          payload: data,
        });

        toastSuccessTranslated('Success');
        return data;
      } catch (error: any) {
        toastErrorTranslated(error);
        return null;
      }
    },
    [dispatch],
  );

  const updateCompanyPlatformFeatures = useCallback(
    async (platformFeatures: PlatformFeatureType[]) => {
      try {
        const { data } =
          await updateCompanyPlatformFeaturesApi(platformFeatures);

        dispatch({
          type: Actions.UPDATE_COMPANY_PLATFORM_FEATURES_SUCCESS,
          payload: data,
        });

        toastSuccessTranslated('Success');
        return data;
      } catch (error: any) {
        toastErrorTranslated(error);
        return null;
      }
    },
    [dispatch],
  );

  const bulkInviteMembers = useCallback(
    async (membersData: BulkInviteUsersDTO) => {
      try {
        const { data: members } = await client.company.bulkInviteUsers({
          body: membersData,
        });
        newDispatch(mainActions.addMembers(members));

        toastSuccessTranslated('Success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
      }
    },
    [newDispatch],
  );

  return {
    fetchCompanyMembers,
    setCompanyMembers,
    fetchGeneralSettings,
    updateGeneralSettings,
    createCompany,
    removeMember,
    addMember,
    activateCompany,
    deactivateCompany,
    teardownCompany,
    updateCompanyPackages,
    updateCompanyPlatformFeatures,
    updateCompanyPlatformFeaturesMetadata,
    bulkInviteMembers,
  };
};
